import type { WebSocketBaseData, WebSocketResponse } from '../contexts/type';
import type { IWSBaseCachedQueryConfig } from './type';

import { useWebsocketContext } from '../contexts/WebsocketProvider';
import { useRetryScheduler } from './useRetryScheduler';
import { useQueryClient } from '@tanstack/react-query';
import { useFallbackTimer } from './useFallbackTimer';
import { useCallback, useEffect } from 'react';
import { WSStatus } from '../contexts/type';
import { useToast } from '~/hooks/useToast';

export const useWSBaseCachedQuery = <T>(config: IWSBaseCachedQueryConfig<T>) => {
  const queryClient = useQueryClient();
  const ws = useWebsocketContext();

  const { shopifyToast } = useToast();

  const {
    wsMessageKey,
    queryKeyPrefix,
    label,
    cache,
    emptyData,
    upsertCache,
    findCacheKeyByRequestId,
    refetchTimeout = 10_000,
    retryInterval = 5_000,
    maxRetries = 10,
  } = config;

  const refetchMatchingQuery = useCallback(
    (requestId: string) => {
      const matchedKey = findCacheKeyByRequestId(requestId);
      if (!matchedKey) return;

      queryClient.refetchQueries({
        predicate: (query) => {
          const [queryKey, queryValue] = query.queryKey;
          return JSON.stringify(queryValue) === matchedKey && queryKey === queryKeyPrefix;
        },
      });
    },
    [findCacheKeyByRequestId, queryKeyPrefix, queryClient],
  );

  const { schedule: scheduleRetry, cancel: cancelRetry } = useRetryScheduler(
    cache,
    refetchMatchingQuery,
    {
      interval: retryInterval,
      maxRetries,
    },
  );

  const handleFallback = useCallback(
    (requestId: string) => {
      refetchMatchingQuery(requestId);
      scheduleRetry(requestId);
    },
    [refetchMatchingQuery, scheduleRetry],
  );

  const { cancel: cancelFallback } = useFallbackTimer(cache, handleFallback, refetchTimeout);

  const handleMessage = useCallback(
    (response: WebSocketResponse<WebSocketBaseData>) => {
      if (!response?.Data) return;

      const { RequestID } = response;

      switch (response.Data.Status) {
        case WSStatus.SUCCESS:
          cancelFallback(RequestID);
          cancelRetry(RequestID);
          refetchMatchingQuery(RequestID);
          break;

        case WSStatus.ERROR: {
          const matchedKey = findCacheKeyByRequestId(RequestID);
          if (!matchedKey) return;
          upsertCache(matchedKey, { requestId: RequestID, data: emptyData });
          shopifyToast(`Failed to fetch ${label}`, { isError: true });
          break;
        }
      }
    },
    [
      refetchMatchingQuery,
      cancelFallback,
      cancelRetry,
      findCacheKeyByRequestId,
      upsertCache,
      emptyData,
      label,
      shopifyToast,
    ],
  );

  useEffect(() => {
    ws.onMessage(wsMessageKey, handleMessage);
    return () => ws.removeMessageListener(wsMessageKey, handleMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { cache };
};
